import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Met a jour l'icone d'onglet du navigateur avec le logo du club (upload admin, configurable
// par club - pas de favicon.ico statique dans public/, donc mis a jour au runtime des que
// club-info renvoie une logo_url). Cree le <link rel="icon"> s'il n'existe pas encore.
const FAVICON_TYPE_BY_EXT = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
  webp: 'image/webp', ico: 'image/x-icon',
};

export function setFavicon(url) {
  if (!url) return;
  const ext = url.split('?')[0].split('.').pop().toLowerCase();
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = FAVICON_TYPE_BY_EXT[ext] || 'image/png';
  link.href = url;
}

export const DIVING_LEVELS = [
  "Débutant", "PE20", "PE40", "PA20", "PA40", 
  "N1", "N2", "N3", "N4", "MF1", "MF2"
];

export const EVALUATION_LEVELS = {
  0: { label: 'Non évalué', color: 'gray' },
  1: { label: 'Non acquis', color: 'red' },
  2: { label: 'En cours', color: 'amber' },
  3: { label: 'Acquis', color: 'emerald' },
  4: { label: 'Maîtrisé', color: 'sky' }
};

// 18/09/2026 (signalement du club : "peux-tu modifier l'affichage check par cheque").
//
// Le libelle n'etait que la partie visible. Le backend CONVERTIT `cheque` en `check` a
// l'inscription (`if payment == "cheque": payment = "check"`, et `cagnotte` -> `online`) : la
// valeur canonique en base est donc `check`, mais tout le frontend avait ete ecrit contre
// `cheque`. Mesure du 18/09 : **check 151, cheque 2** - trois tables de libelles distinctes
// ignoraient la graphie majoritaire, et six conditions `=== 'cheque'` rataient 151 adhesions
// sur 153. Consequence invisible mais serieuse : le bouton « Voir le cheque » et
// l'avertissement « photo non deposee » ne s'affichaient JAMAIS pour ces adhesions.
//
// Source unique plutot que trois tables rapiecees : la prochaine valeur ajoutee cote backend
// n'a qu'un endroit a mettre a jour.
export const PAYMENT_METHOD_LABELS = {
  cb: 'Carte bancaire',
  cb3fois: 'CB 3 fois',
  cbnfois: 'CB plusieurs fois',
  check: 'Chèque',
  cheque: 'Chèque',      // graphie historique, encore presente sur 2 adhesions
  online: 'Cagnotte en ligne',
  especes: 'Espèces',
  virement: 'Virement',
  helloasso: 'HelloAsso',
  autre: 'Autre',
};

/** Libelle lisible d'un mode de paiement ; rend la valeur brute si elle est inconnue, pour ne
 *  jamais masquer une donnee qu'on n'aurait pas prevue. */
export const labelPaiement = (valeur) => PAYMENT_METHOD_LABELS[valeur] || valeur || '—';

/** 1.57.0.0 : vrai quand la photo de profil est l'avatar plongee pose a la suppression de la
 *  photo (AVATARS_PLONGEE cote serveur, dossier uploads/avatars/). */
export const estAvatarGenerique = (photoUrl) => String(photoUrl || '').startsWith('/api/uploads/avatars/');

/** Vrai pour un reglement par cheque, QUELLE QUE SOIT la graphie stockee. */
export const estPaiementCheque = (sub) => {
  const v = (sub?.payment_method || sub?.mode_paiement || '').toLowerCase();
  return v === 'check' || v === 'cheque';
};

// 23/09/2026 (signalement du club "ajout de photo sur les detendeurs : rien dans les logs du
// conteneur") : une vraie photo de telephone (plusieurs Mo) envoyee telle quelle sur une
// connexion mobile faible peut etre coupee AVANT d'atteindre le serveur - rien ne l'y indique
// (pas d'erreur backend, aucune trace de requete), le navigateur affiche juste le message
// d'echec generique. Reduire le fichier cote navigateur avant l'envoi (meme cible que la
// reduction serveur, PHOTO_MAX_DIMENSION=1000 / PHOTO_JPEG_QUALITY=85 dans server_mysql.py)
// rend l'upload plus rapide et moins expose a une coupure, sans jamais bloquer l'envoi si la
// compression echoue (HEIC non decode par le navigateur, etc.) - le fichier original part alors
// tel quel, comme avant ce correctif.
export async function compressImage(file, { maxDimension = 1000, quality = 0.85, seuilOctets = 700 * 1024 } = {}) {
  if (!file || !file.type?.startsWith('image/') || file.size <= seuilOctets) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const ratio = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * ratio);
    const h = Math.round(bitmap.height * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;
    const nomBase = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${nomBase}.jpg`, { type: 'image/jpeg' });
  } catch (e) {
    console.warn('Compression image impossible, envoi du fichier original :', e);
    return file;
  }
}

/** Message d'erreur d'un upload qui a echoue : le detail du backend s'il existe, sinon un
 *  message distinct pour une coupure reseau (pas de reponse du tout) plutot qu'un texte
 *  generique qui ne dit rien de ce qui vient de se passer. */
export const messageErreurUpload = (error, texteParDefaut) => {
  const detail = error?.response?.data?.detail;
  if (detail) return detail;
  if (!error?.response) return "Connexion interrompue pendant l'envoi. Vérifiez votre connexion et réessayez.";
  return texteParDefaut;
};

// 1.48.1.0 (demande du club "mettre en avant les adhesions sans numero de licence ou avec un
// numero non valide") : forme d'un numero de licence FFESSM, verifiee sur les fiches reelles
// (lettre-2 chiffres-6 ou 7 chiffres, ex. A-14-646953, A-26-4309636). Meme regle cote serveur
// (LICENCE_FORMAT, compteur du tableau de bord). null = numero correct.
export const LICENCE_FORMAT = /^[A-Z]-\d{2}-\d{6,7}$/;
export const etatLicence = (numero) => {
  const v = (numero || '').trim().toUpperCase();
  if (!v) return 'absente';
  return LICENCE_FORMAT.test(v) ? null : 'invalide';
};

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 03/10/2026 (QR code d'un eleve scanne sans etre connecte) : destination a reprendre a la fin
// d'une connexion. La page de connexion la memorise (2 minutes) avant d'ouvrir la session, et la
// protection des pages publiques (App.jsx, PublicRoute) la suit au lieu de renvoyer vers "/".
const DESTINATION_APRES_CONNEXION = 'cesam_destination_apres_connexion';
export const memoriserDestination = (cible) => {
  try { sessionStorage.setItem(DESTINATION_APRES_CONNEXION, JSON.stringify({ cible, t: Date.now() })); } catch { /* stockage indisponible */ }
};
export const destinationApresConnexion = (search) => {
  let cible = new URLSearchParams(search || '').get('redirect');
  try {
    const m = JSON.parse(sessionStorage.getItem(DESTINATION_APRES_CONNEXION) || 'null');
    if (!cible && m && Date.now() - m.t < 120000) cible = m.cible;
  } catch { /* stockage indisponible */ }
  return cible && cible.startsWith('/') && !cible.startsWith('//') ? cible : '/';
};
